import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, AlertTriangle, TrendingDown, Ship, PackageX, Activity, ClipboardList, CalendarClock, History, CheckCircle2, Wrench } from "lucide-react";

const CFG = {
  SEM_ESTOQUE: { icon: PackageX }, ESTOQUE_CRITICO: { icon: AlertTriangle }, RISCO_RUPTURA: { icon: TrendingDown },
  IMPORTACAO: { icon: Ship }, CONSUMO_ANORMAL: { icon: Activity }, INVENTARIO_DIVERGENTE: { icon: ClipboardList },
  MAQUINA_FUTURA: { icon: CalendarClock }, CONFIRMACAO_USO_PENDENTE: { icon: Wrench },
};
const NIVEL = {
  CRITICO: "border-rose-800/60 bg-rose-950/30 text-rose-300",
  ALTO: "border-orange-800/60 bg-orange-950/30 text-orange-300",
  MEDIO: "border-amber-800/60 bg-amber-950/30 text-amber-300",
  INFO: "border-sky-800/60 bg-sky-950/30 text-sky-300",
};
const ST = {
  NOVO: { label: "Novo", cls: "bg-sky-950/60 text-sky-300 border-sky-700/60" },
  EM_ANALISE: { label: "Em análise", cls: "bg-amber-950/60 text-amber-300 border-amber-700/60" },
  RESOLVIDO: { label: "Resolvido", cls: "bg-emerald-950/60 text-emerald-300 border-emerald-700/60" },
};
const STATUS_LABEL = { NOVO: "Novo", EM_ANALISE: "Em análise", RESOLVIDO: "Resolvido" };

export default function Alerts() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [alerts, setAlerts] = useState(null);
  const [filter, setFilter] = useState("ATIVOS");

  const load = () => api.get("/alerts").then((r) => setAlerts(r.data));
  useEffect(() => { load(); }, []);

  const canManage = can("ALMOXARIFADO", "MANUTENCAO", "COMPRAS");

  const changeStatus = async (a, status) => {
    try { await api.put("/alerts/status", { key: a.key, status }); toast.success(`Alerta marcado como ${STATUS_LABEL[status]}.`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (alerts === null) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 bg-slate-800" />)}</div>;

  const shown = alerts.filter((a) => filter === "TODOS" ? true : filter === "ATIVOS" ? a.status !== "RESOLVIDO" : a.status === filter);
  const counts = alerts.reduce((acc, x) => ({ ...acc, [x.status]: (acc[x.status] || 0) + 1 }), {});

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-slate-50 flex items-center gap-2"><Bell className="h-7 w-7 text-amber-400" /> Central de Alertas</h1><p className="text-slate-400 text-sm">Estados: Novo → Em análise → Resolvido, com histórico de quem alterou e quando.</p></div>
        <div className="flex gap-2 text-sm">
          <span className="rounded-md bg-sky-950/40 border border-sky-800/50 px-3 py-1 text-sky-300">Novos: {counts.NOVO || 0}</span>
          <span className="rounded-md bg-amber-950/40 border border-amber-800/50 px-3 py-1 text-amber-300">Em análise: {counts.EM_ANALISE || 0}</span>
          <span className="rounded-md bg-emerald-950/40 border border-emerald-800/50 px-3 py-1 text-emerald-300">Resolvidos: {counts.RESOLVIDO || 0}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {["ATIVOS", "NOVO", "EM_ANALISE", "RESOLVIDO", "TODOS"].map((f) => (
          <Button key={f} data-testid={`alert-filter-${f}`} size="sm" variant={filter === f ? "default" : "secondary"} onClick={() => setFilter(f)} className={filter === f ? "bg-sky-700" : ""}>
            {f === "ATIVOS" ? "Ativos" : f === "TODOS" ? "Todos" : STATUS_LABEL[f]}
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        {shown.map((a, i) => {
          const Icon = (CFG[a.tipo] || CFG.ESTOQUE_CRITICO).icon;
          const st = ST[a.status] || ST.NOVO;
          return (
            <Card key={a.key + i} data-testid={`alert-${a.tipo}-${i}`} className={`card-metal p-4 border ${NIVEL[a.nivel]}`}>
              <div className="flex items-start gap-3">
                <Icon className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => a.part_id && navigate(`/pecas/${a.part_id}`)}>
                  <div className="font-semibold text-slate-100">{a.titulo}</div>
                  <div className="text-sm text-slate-400">{a.mensagem}</div>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${st.cls}`} data-testid={`alert-status-${i}`}>{st.label}</span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 pl-8">
                <div className="flex items-center gap-2">
                  {canManage ? (
                    <Select value={a.status} onValueChange={(v) => changeStatus(a, v)}>
                      <SelectTrigger data-testid={`alert-status-select-${i}`} className="h-8 w-40 bg-slate-950 border-slate-700 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                        <SelectItem value="NOVO">Novo</SelectItem>
                        <SelectItem value="EM_ANALISE">Em análise</SelectItem>
                        <SelectItem value="RESOLVIDO">Resolvido</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : <span className="text-xs text-slate-500">Somente leitura</span>}
                </div>
                {a.tipo === "CONFIRMACAO_USO_PENDENTE" && (
                  <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-600 text-xs" data-testid={`alert-confirmar-destino-${i}`} onClick={() => navigate("/manutencao-pecas")}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirmar destino
                  </Button>
                )}
                {a.history?.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-400" data-testid={`alert-history-btn-${i}`}><History className="h-3.5 w-3.5 mr-1" /> Histórico ({a.history.length})</Button>
                    </PopoverTrigger>
                    <PopoverContent className="bg-slate-900 border-slate-700 text-slate-100 w-72">
                      <div className="text-xs font-mono uppercase text-slate-500 mb-2">Histórico de alterações</div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {[...a.history].reverse().map((h, j) => (
                          <div key={j} className="text-sm border-b border-slate-800 pb-1">
                            <div className="text-slate-200">{STATUS_LABEL[h.status] || h.status}</div>
                            <div className="text-xs text-slate-500">{h.usuario_nome} · {fmtDateTime(h.data)}</div>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </Card>
          );
        })}
        {shown.length === 0 && <div className="text-center py-16 text-slate-500">Nenhum alerta neste filtro.</div>}
      </div>
    </div>
  );
}
